/**
 * Login Gate — Firebase Authentication gate for the web surface.
 *
 * Wraps Firebase Auth in a full-page overlay matching the Botanical theme.
 * Supports email/password, Google, Apple, Facebook, X/Twitter sign-in.
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth'
import { auth } from './firebase.js'
import { upsertUserOnLogin } from './profile-service.js'

const googleProvider = new GoogleAuthProvider()
const appleProvider = new OAuthProvider('apple.com')
const facebookProvider = new FacebookAuthProvider()
facebookProvider.addScope('email')
const twitterProvider = new TwitterAuthProvider()

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

export function getCurrentUser() {
  return auth.currentUser
}

export async function clearSession() {
  await firebaseSignOut(auth)
}

// ---------------------------------------------------------------------------
// Overlay HTML
// ---------------------------------------------------------------------------

function buildOverlay(tab) {
  const el = document.createElement('div')
  el.id = 'gate-overlay'
  el.className = 'gate-overlay'
  el.setAttribute('role', 'dialog')
  el.setAttribute('aria-modal', 'true')
  el.setAttribute('aria-label', 'Sign in to Hormiga Dormida')

  el.innerHTML = `
    <div class="gate-card">
      <div class="gate-brand">
        <svg viewBox="740 540 650 730" width="42" height="42" xmlns="http://www.w3.org/2000/svg">
          <path fill="#fe5d5c" d="M1138.43 556.307C1149.41 555.305 1159.72 554.897 1168.97 561.921C1180.94 571.002 1181.11 584.893 1169.82 594.595C1151.49 610.346 1126.11 612.653 1102.97 613.108C1078.24 613.039 1060.71 632.913 1049.18 652.833C1033.9 679.236 1030.04 707.203 1032.22 737.233C1068.19 729.995 1087.41 728.913 1123.21 736.626C1140.77 703.701 1157.53 681.223 1189.23 659.922C1227.79 635.308 1270.15 622.998 1316.04 630.774C1337.91 634.479 1376.46 658.284 1343.91 678.92C1331.74 686.64 1297.78 682.129 1285.37 675.268C1256.92 659.547 1240.29 660.67 1211.9 676.287C1186.28 690.185 1162.34 720.16 1154.18 749.001C1197.96 773.038 1219.54 792.022 1245.15 835.474C1251.56 848.231 1255.55 858.639 1259.87 872.22C1227.93 881.255 1205.2 874.602 1176.29 860.827C1194.37 892.105 1232.09 895.732 1264.81 893.81C1265.11 902.854 1265.66 912.88 1264.24 921.753C1252.61 994.498 1166.09 1012.79 1104.45 1003.31C1067.28 998.264 1036.74 985.08 1006.52 963.288C964.724 933.141 936.113 886.033 944.071 833.073C950.005 793.583 970.065 773.213 1001.18 750.297C1000.75 739.006 1000.77 728.861 1001.62 717.579C1007.06 645.396 1064.29 568.8 1138.43 556.307Z"/>
          <path fill="#f45b69" d="M971.414 953.796C978.679 958.749 986.873 967.633 994.419 973.223C1027.44 997.846 1066.72 1012.7 1107.77 1016.09C1123.86 1032.91 1147.25 1068.41 1157.82 1088.58C1182.32 1135.38 1189.47 1204.96 1173.27 1255.55C1155.99 1257.72 1140.48 1259.7 1123.04 1258.33C1121.66 1258.27 1120.29 1258.16 1118.92 1258.01C1051.17 1250.68 989.028 1217 945.888 1164.25C920.095 1132.3 904.64 1094.01 909.471 1052.58C914.644 1008.2 937.908 980.619 971.414 953.796Z"/>
          <path fill="#4b50f5" d="M932.106 785.045L932.934 786.437C925.228 799.604 921.076 816.14 919.129 831.161C914.024 870.533 923.347 905.278 947.657 936.6C925.987 953.986 909.771 970.661 897.546 996.141C878.324 1035.63 875.848 1081.21 890.68 1122.55C911.493 1180.68 958.524 1227.63 1013.7 1253.93C990.412 1251.62 976.139 1248.97 953.557 1242.05C946.961 1239.99 940.439 1237.7 934.002 1235.19C874.3 1211.76 821.674 1161.31 798.503 1101.46C748.486 972.266 805.238 839.048 932.106 785.045Z"/>
        </svg>
        <span class="gate-brand-name">Hormiga Dormida</span>
      </div>

      <div class="gate-tabs">
        <button class="gate-tab ${tab === 'signin' ? 'active' : ''}" data-tab="signin">Sign in</button>
        <button class="gate-tab ${tab === 'signup' ? 'active' : ''}" data-tab="signup">Create account</button>
      </div>

      <form class="gate-form" id="gate-form" autocomplete="off" novalidate>
        <div class="gate-field" id="gate-name-field" style="${tab === 'signin' ? 'display:none' : ''}">
          <label class="gate-label" for="gate-name">Display name</label>
          <input class="gate-input" type="text" id="gate-name" autocomplete="name" placeholder="Your name" />
        </div>
        <div class="gate-field">
          <label class="gate-label" for="gate-email">Email</label>
          <input class="gate-input" type="email" id="gate-email" autocomplete="email" placeholder="you@example.com" required />
        </div>
        <div class="gate-field">
          <label class="gate-label" for="gate-password">Password</label>
          <input
            class="gate-input" type="password" id="gate-password"
            autocomplete="${tab === 'signin' ? 'current-password' : 'new-password'}"
            placeholder="${tab === 'signin' ? 'Enter password' : 'Create password (min 6 chars)'}"
            required
          />
        </div>
        <div class="gate-error gate-error--hidden" id="gate-error" role="alert"></div>
        <div class="gate-submit-wrap">
          <button type="submit" class="gate-submit" id="gate-submit">
            ${tab === 'signin' ? 'Sign in' : 'Create account'}
          </button>
          <img src="/miga/miga-dangling.svg" alt="" class="gate-miga" aria-hidden="true" />
        </div>
      </form>

      <div class="gate-divider"><span>or</span></div>

      <div class="gate-social-row">
        <button class="gate-google" id="gate-google">
          <span class="gate-social-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
          </span>
          <span class="gate-spinner" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="#dadce0" stroke-width="2"/>
              <path d="M8 1.5A6.5 6.5 0 0114.5 8" stroke="#4285F4" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          Continue with Google
        </button>

        <button class="gate-apple" id="gate-apple">
          <span class="gate-social-icon" aria-hidden="true">
            <svg width="16" height="18" viewBox="0 0 17 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.94 10.57c-.02-2.26 1.84-3.34 1.92-3.4-1.05-1.53-2.68-1.74-3.26-1.76-1.39-.14-2.71.82-3.41.82-.7 0-1.79-.8-2.94-.78-1.51.02-2.91.88-3.69 2.24-1.57 2.73-.4 6.78 1.13 9 .75 1.08 1.64 2.3 2.81 2.26 1.13-.05 1.55-.73 2.92-.73 1.36 0 1.74.73 2.93.7 1.21-.01 1.98-1.1 2.73-2.19.86-1.26 1.21-2.47 1.23-2.54-.03-.01-2.37-.91-2.37-3.62zM12.73 3.63c.62-.76 1.04-1.81.93-2.86-.89.04-1.98.6-2.62 1.35-.57.66-1.07 1.72-.94 2.74 1 .08 2.01-.5 2.63-1.23z"/>
            </svg>
          </span>
          <span class="gate-spinner" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
              <path d="M8 1.5A6.5 6.5 0 0114.5 8" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          Continue with Apple
        </button>

        <button class="gate-facebook" id="gate-facebook">
          <span class="gate-social-icon" aria-hidden="true">
            <svg width="20" height="12" viewBox="0 0 36 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 0C14.4 0 12 3.2 10.8 5.6C9.6 3.2 7.2 0 3.6 0C1.2 0 0 1.6 0 4C0 8 4 16 10.8 20C4.8 16 2.4 10 2.4 6.4C2.4 4.8 3.2 4 4 4C5.6 4 7.6 6.8 8.8 10C10 13.2 10.4 16 10.8 16C11.2 16 11.6 13.2 12.8 10C14 6.8 16 4 17.6 4C18.4 4 18.8 4.4 18.8 5.2C18.8 8 14.4 14.4 10.8 20C17.6 16 22 8 22 4C22 1.6 20.4 0 18 0Z M32.4 0C28.8 0 26.4 3.2 25.2 5.6C24 3.2 21.6 0 18 0C15.6 0 14.4 1.6 14.4 4C14.4 8 18.4 16 25.2 20C19.2 16 16.8 10 16.8 6.4C16.8 4.8 17.6 4 18.4 4C20 4 22 6.8 23.2 10C24.4 13.2 24.8 16 25.2 16C25.6 16 26 13.2 27.2 10C28.4 6.8 30.4 4 32 4C32.8 4 33.2 4.4 33.2 5.2C33.2 8 28.8 14.4 25.2 20C32 16 36 8 36 4C36 1.6 34.8 0 32.4 0Z" fill="white"/>
            </svg>
          </span>
          <span class="gate-spinner" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="#dadce0" stroke-width="2"/>
              <path d="M8 1.5A6.5 6.5 0 0114.5 8" stroke="#1877F2" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          Continue with Meta
        </button>

        <button class="gate-twitter" id="gate-twitter">
          <span class="gate-social-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
            </svg>
          </span>
          <span class="gate-spinner" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="#dadce0" stroke-width="2"/>
              <path d="M8 1.5A6.5 6.5 0 0114.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          Continue with X
        </button>
      </div>
    </div>
  `

  return el
}

// ---------------------------------------------------------------------------
// Mount and wire
// ---------------------------------------------------------------------------

function mountOverlay(overlay, currentTab, onSuccess) {
  document.body.appendChild(overlay)
  document.body.style.overflow = 'hidden'

  const form = overlay.querySelector('#gate-form')
  const emailInput = overlay.querySelector('#gate-email')
  const passwordInput = overlay.querySelector('#gate-password')
  const nameInput = overlay.querySelector('#gate-name')
  const errorEl = overlay.querySelector('#gate-error')
  const submitBtn = overlay.querySelector('#gate-submit')
  const googleBtn = overlay.querySelector('#gate-google')
  const appleBtn = overlay.querySelector('#gate-apple')
  const facebookBtn = overlay.querySelector('#gate-facebook')
  const twitterBtn = overlay.querySelector('#gate-twitter')
  const tabs = overlay.querySelectorAll('.gate-tab')

  setTimeout(() => emailInput?.focus(), 80)

  function showError(msg) {
    errorEl.textContent = msg
    errorEl.classList.remove('gate-error--hidden')
  }

  function hideError() {
    errorEl.classList.add('gate-error--hidden')
  }

  function setLoading(loading) {
    submitBtn.disabled = loading
    googleBtn.disabled = loading
    appleBtn.disabled = loading
    if (facebookBtn) facebookBtn.disabled = loading
    if (twitterBtn) twitterBtn.disabled = loading
    submitBtn.textContent = loading ? '…' : currentTab === 'signin' ? 'Sign in' : 'Create account'
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const newTab = tab.dataset.tab
      if (newTab === currentTab) return
      overlay.remove()
      document.body.style.overflow = ''
      const fresh = buildOverlay(newTab)
      mountOverlay(fresh, newTab, onSuccess)
    })
  })

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    hideError()

    const email = emailInput.value.trim()
    const password = passwordInput.value
    if (!email || !password) {
      showError('Email and password are required.')
      return
    }

    setLoading(true)
    try {
      if (currentTab === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email, password)
        const name = nameInput?.value?.trim()
        if (name) await updateProfile(cred.user, { displayName: name })
      } else {
        await signInWithEmailAndPassword(auth, email, password)
      }
      const authedUser = auth.currentUser
      if (authedUser) void upsertUserOnLogin(authedUser)
      dismissOverlay(overlay)
      onSuccess()
    } catch (err) {
      setLoading(false)
      showError(friendlyError(err))
    }
  })

  googleBtn.addEventListener('click', async () => {
    hideError()
    googleBtn.dataset.loading = 'true'
    googleBtn.disabled = true
    try {
      const result = await signInWithPopup(auth, googleProvider)
      void upsertUserOnLogin(result.user)
      dismissOverlay(overlay)
      onSuccess()
    } catch (err) {
      googleBtn.dataset.loading = 'false'
      googleBtn.disabled = false
      showError(friendlyError(err))
    }
  })

  appleBtn.addEventListener('click', async () => {
    hideError()
    appleBtn.dataset.loading = 'true'
    appleBtn.disabled = true
    appleProvider.addScope('email')
    appleProvider.addScope('name')
    try {
      const result = await signInWithPopup(auth, appleProvider)
      void upsertUserOnLogin(result.user)
      dismissOverlay(overlay)
      onSuccess()
    } catch (err) {
      appleBtn.dataset.loading = 'false'
      appleBtn.disabled = false
      showError(friendlyError(err))
    }
  })

  facebookBtn?.addEventListener('click', async () => {
    hideError()
    facebookBtn.dataset.loading = 'true'
    facebookBtn.disabled = true
    try {
      const result = await signInWithPopup(auth, facebookProvider)
      void upsertUserOnLogin(result.user)
      dismissOverlay(overlay)
      onSuccess()
    } catch (err) {
      facebookBtn.dataset.loading = 'false'
      facebookBtn.disabled = false
      showError(friendlyError(err))
    }
  })

  twitterBtn?.addEventListener('click', async () => {
    hideError()
    twitterBtn.dataset.loading = 'true'
    twitterBtn.disabled = true
    try {
      const result = await signInWithPopup(auth, twitterProvider)
      void upsertUserOnLogin(result.user)
      dismissOverlay(overlay)
      onSuccess()
    } catch (err) {
      twitterBtn.dataset.loading = 'false'
      twitterBtn.disabled = false
      showError(friendlyError(err))
    }
  })
}

function dismissOverlay(overlay) {
  overlay.classList.add('gate-overlay--leaving')
  document.body.style.overflow = ''
  setTimeout(() => overlay.remove(), 260)
}

function friendlyError(err) {
  const map = {
    'auth/user-not-found': 'No account found with that email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/account-exists-with-different-credential': 'An account with this email already exists using a different sign-in method.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed.',
    'auth/popup-blocked': 'Popup was blocked. Please allow popups and try again.',
    'auth/network-request-failed': 'Network error. Check your connection.'
  }
  return map[err?.code] ?? 'Something went wrong. Please try again.'
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function requireLogin() {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubscribe()
        void upsertUserOnLogin(user)
        resolve(user)
        return
      }
      const overlay = buildOverlay('signin')
      mountOverlay(overlay, 'signin', () => {
        const currentUser = auth.currentUser
        if (currentUser) {
          unsubscribe()
          resolve(currentUser)
        }
      })
    })
  })
}
